import React, { useContext, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Table, Menu, Icon, Modal, Input, Image } from "semantic-ui-react";
import styled from "styled-components";
import media from "styled-media-query";
import { ThemeContext } from "styled-components";
import { Button } from "components/shared";

// How long a row stays highlighted after its value changes (highlightOnChange
// tables only - see ParametersTable's prop). The flash-in is instant (no
// transition) so the change is unmistakable the moment it happens; only the
// fade back to normal is animated, over FLASH_FADE_MS.
const FLASH_DURATION_MS = 2000;
const FLASH_FADE_MS = 600;

const styles = {
  header: {
    padding: "8px 50px 8px 50px",
  },
};

const CustomTable = styled((props, className) => (
  <Table className={className} {...props} />
))`
  margin: 0rem;
  ${media.lessThan("small")`
    table-layout: fixed;
  `}
}
`;

const CustomCell = styled((props, className) => (
  <Table.Cell className={className} {...props} />
))`
  font-size: 1.1rem;
  word-wrap: break-word;
  width: 25%;
  vertical-align: top;
  border-bottom: 2px solid black;

  ${media.lessThan("small")`
  font-size: 0.8rem;
  padding: 0.5rem !important;
  `}
`;

const LogsNavigator = styled.div`
  display: flex;
  justify-content: flex-end;

  ${media.lessThan("768px")`
  justify-content: center;
  `}
`;

const ServiceLogImages = ({ images, onImageClick }) => {
  const logImages = images;

  return (
    <div>
      {logImages.length > 0 &&
        logImages.map((image) => {
          const isPdf = image.toLowerCase().endsWith(".pdf");
          return (
            <Icon
              bordered
              inverted
              key={image}
              color="teal"
              style={{ margin: "0px 10px 0px 0px" }}
              name={isPdf ? "file pdf outline" : "image"}
              onClick={() => {
                // PDFs can't render inside the <Image> preview modal - open
                // them directly instead.
                if (isPdf) {
                  window.open(image, "_blank", "noopener,noreferrer");
                } else {
                  onImageClick(image);
                }
              }}
            />
          );
        })}
    </div>
  );
};

// One table row, with an optional "flash blue then fade back" highlight when
// its value changes - e.g. a Digital Input flips CW -> CCW and the operator
// should notice the moment it happened, not have to spot it in a wall of
// otherwise-identical rows. Needs its own component (not just a function
// called from a .map()) so the previous-value comparison across renders can
// live in a ref tied to this specific row's identity, via ParametersTable's
// stable (value-independent) key for highlightOnChange tables.
const Row = ({
  p,
  cellKeys,
  boldIndexes,
  cellStyle,
  headerStyle,
  editableCell,
  onEditClick,
  onImageClick,
  highlightOnChange,
  flashColor,
}) => {
  const previousValue = useRef(p.value);
  const isFirstRender = useRef(true);
  const [flashing, setFlashing] = useState(false);

  useEffect(() => {
    if (!highlightOnChange) return;
    if (isFirstRender.current) {
      isFirstRender.current = false;
      previousValue.current = p.value;
      return;
    }
    if (p.value === previousValue.current) return;
    previousValue.current = p.value;
    setFlashing(true);
    const timer = setTimeout(() => setFlashing(false), FLASH_DURATION_MS);
    return () => clearTimeout(timer);
  }, [p.value, highlightOnChange]);

  const rowStyle = highlightOnChange
    ? {
        backgroundColor: flashing ? flashColor : "transparent",
        transition: flashing ? "none" : `background-color ${FLASH_FADE_MS}ms ease`,
      }
    : {};

  return (
    <Table.Row style={rowStyle}>
      {cellKeys.map((key, index) => {
        let val;
        if (key == "photo" && p[key] != null) {
          val = (
            <ServiceLogImages images={p[key]} onImageClick={onImageClick} />
          )
        } else {
          if (key == "value" && p[key] < 0) {
            val = p.default;
          } else {
            val = p[key];
          }
        }

        const fontStyle =
          boldIndexes.indexOf(index) > -1 ? { fontWeight: "bold" } : {};
        const alignStyle =
          Object.keys(p).length === 2 && index === 1
            ? { textAlign: "right" }
            : {};
        return (
          <CustomCell
            collapsing={false}
            key={key}
            style={{
              ...cellStyle,
              ...alignStyle,
              ...fontStyle,
              ...headerStyle,
            }}
          >
            {val}
          </CustomCell>
        );
      })}
      {editableCell && (
        <Table.Cell style={{ paddingRight: 0 }}>
          <Button
            icon
            secondary
            style={{ backgroundcolor: "transparent" }}
            onClick={() => onEditClick(p)}
          >
            <Icon name="pencil" />
            <span style={{ marginLeft: 6 }}>Edit</span>
          </Button>
        </Table.Cell>
      )}
    </Table.Row>
  );
};

const ParametersTable = ({
  params,
  paginated,
  columnNames,
  columnKeys,
  editable,
  numberOfItemsPerPage,
  cellStyle: cellStyleProp,
  boldIndexes,
  onEditParameter,
  highlightOnChange,
}) => {
  const NUMBER_OF_ITEMS = numberOfItemsPerPage || params.length;
  const themeContext = useContext(ThemeContext);
  const [lastItemIndex, setLastItemIndex] = useState(NUMBER_OF_ITEMS - 1);
  const [data, setData] = useState([]);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [editedParameter, setEditedParameter] = useState(null);
  const [editedNewValue, setEditedNewValue] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [image, setImage] = useState("");
  const editInputRef = useRef();

  useEffect(() => {
    setDisplayedItems(
      startIndexOfSlice(lastItemIndex, NUMBER_OF_ITEMS),
      lastItemIndex
    );
  }, [params, lastItemIndex, NUMBER_OF_ITEMS, paginated]);

  const startIndexOfSlice = (lastIndex, totalItems) =>
    lastIndex === totalItems - 1 ? 0 : lastIndex - totalItems + 1;

  const setDisplayedItems = (startIndex, lastIndex) => {
    params.length && paginated
      ? setData(params.slice(startIndex, lastIndex + 1))
      : setData(params);
  };

  const cellStyle = {
    ...styles.cell,
    ...cellStyleProp,
    borderColor: themeContext.controlsBackgroundColor,
    color: themeContext.textDark,
  };

  const headerStyle = {
    ...cellStyleProp,
    color: themeContext.textDark,
    minWidth: "1rem",
  };

  const onPreviousPage = () => {
    if (lastItemIndex < NUMBER_OF_ITEMS) {
      return;
    }
    setLastItemIndex(lastItemIndex - NUMBER_OF_ITEMS);
  };

  const onNextPage = () => {
    if (lastItemIndex < params.length && params[lastItemIndex + 1]) {
      setLastItemIndex(lastItemIndex + NUMBER_OF_ITEMS);
    }
  };

  const onEditClick = (param) => {
    setConfirmationVisible(true);
    setEditedNewValue(param.value < 0 ? param.default : param.value);
    setEditedParameter(param);
  };

  const onCancelEditDialog = () => {
    setConfirmationVisible(false);
    setEditedParameter(null);
  };

  // Pre-fills the input with the register's documented default, same as
  // clicking into the field and typing it - doesn't write anything by
  // itself, Save still has to be clicked, so a misclick here can't push a
  // value to the turbine.
  const onResetToDefault = () => {
    setEditedNewValue(editedParameter.default);
    if (editInputRef.current && editInputRef.current.inputRef.current) {
      editInputRef.current.inputRef.current.value = editedParameter.default;
    }
  };

  const onConfirmEditParam = () => {
    const newValue = editInputRef.current.inputRef.current.valueAsNumber;
    onEditParameter(editedParameter, newValue);
    setConfirmationVisible(false);
    setEditedParameter(null);
  };

  const onEditedNewValueChange = (e, d) => {
    // parseFloat, not parseInt - these are already-scaled real-world values
    // (e.g. a scale-100 register reads as "52.00 Hz"), and truncating to an
    // integer here would silently throw away exactly the precision the
    // operator typed.
    let newVal = parseFloat(d.value);
    if (!d.value.length || Number.isNaN(newVal)) {
      newVal = undefined;
    }
    setEditedNewValue(newVal);
  };

  const onImageClick = (image) => {
    setImage(image);
    setShowImageModal(true);
  };

  function renderRow(p, index) {
    const editableCell = typeof editable === "boolean" ? editable : editable(p);
    const cellKeys = columnKeys ? columnKeys : Object.keys(p);
    // highlightOnChange tables need a key that survives a value change (so
    // the same Row instance - and its "previous value" ref - sticks around
    // to notice the change) rather than the default key, which is derived
    // from the value itself and would remount a fresh Row with no memory of
    // what it used to be.
    const key = highlightOnChange
      ? p.name != null ? p.name : index
      : Object.values(p).join(",");
    return (
      <Row
        key={key}
        p={p}
        cellKeys={cellKeys}
        boldIndexes={boldIndexes}
        cellStyle={cellStyle}
        headerStyle={headerStyle}
        editableCell={editableCell}
        onEditClick={onEditClick}
        onImageClick={onImageClick}
        highlightOnChange={highlightOnChange}
        flashColor="rgba(33, 150, 243, 0.4)"
      />
    );
  }
  return (
    <>
      <CustomTable columns={4} basic="very" unstackable={true}>
        <Table.Header className="mobile hidden">
          <Table.Row>
            {columnNames &&
              columnNames.map((c) => (
                <Table.HeaderCell key={c} style={headerStyle}>
                  {c}
                </Table.HeaderCell>
              ))}
          </Table.Row>
        </Table.Header>

        <Table.Body
          style={{ backgroundcolor: themeContext.cardBackgroundColor }}
        >
          {data.map(renderRow)}
        </Table.Body>

        {paginated && (
          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell colSpan="5">
                <LogsNavigator>
                  <Menu
                    floated="right"
                    pagination
                    style={{
                      backgroundColor: `${themeContext.primaryButtonColor}`,
                      marginTop: 16,
                      border: 0,
                      borderShadow: 0,
                      boxShadow: 0,
                    }}
                  >
                    <Menu.Item as="a" icon onClick={onPreviousPage}>
                      <Icon
                        name="chevron left"
                        style={{
                          color:
                            lastItemIndex <= NUMBER_OF_ITEMS
                              ? themeContext.primaryInactiveButtonColor
                              : themeContext.primaryButtonText,
                        }}
                      />
                    </Menu.Item>
                    <Menu.Item as="a" icon onClick={onNextPage}>
                      <Icon
                        name="chevron right"
                        style={{
                          color:
                            params[lastItemIndex + 1] === undefined
                              ? themeContext.primaryInactiveButtonColor
                              : themeContext.primaryButtonText,
                        }}
                      />
                    </Menu.Item>
                  </Menu>
                </LogsNavigator>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        )}
      </CustomTable>

      <Modal
        size="tiny"
        open={confirmationVisible}
        onClose={onCancelEditDialog}
      >
        <Modal.Header>{`Edit "${
          editedParameter && editedParameter.name
        }"`}</Modal.Header>
        <Modal.Content>
          {editedParameter && (
            <>
              {editedParameter.number !== undefined && (
                <p>
                  <b>Parameter value: </b>
                  {editedParameter.value < 0 ? editedParameter.default : editedParameter.value}
                </p>
              )}
              {editedParameter.default !== undefined && (
                <p>
                  <b>Default value: </b>
                  {editedParameter.default}
                </p>
              )}
              {editedParameter.unit && (
                <p>
                  <b>Unit: </b>
                  {editedParameter.unit}
                </p>
              )}
              {editedParameter.min !== undefined && (
                <p>
                  <b>Min value: </b>
                  {editedParameter.min}
                </p>
              )}
              {editedParameter.max !== undefined && (
                <p>
                  <b>Max value: </b>
                  {editedParameter.max}
                </p>
              )}
              {editedParameter.address !== undefined && (
                <p>
                  <b>Parameter address: </b>
                  {editedParameter.address}
                </p>
              )}
              {editedParameter.number !== undefined && (
                <p>
                  <b>Parameter number: </b>
                  {editedParameter.number}
                </p>
              )}
              <Input
                ref={editInputRef}
                defaultValue={editedParameter.value < 0 ? editedParameter.default : editedParameter.value}
                type="number"
                label={{ basic: true, content: `${editedParameter.unit}` }}
                labelPosition="right"
                onChange={onEditedNewValueChange}
              />
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button negative onClick={onCancelEditDialog}>
            Cancel
          </Button>
          {editedParameter && editedParameter.default !== undefined && (
            <Button secondary onClick={onResetToDefault}>
              Default
            </Button>
          )}
          <Button
            disabled={
              editedNewValue === undefined ||
              (editedParameter &&
                (editedNewValue < editedParameter.min ||
                  editedNewValue > editedParameter.max))
            }
            positive
            icon="checkmark"
            labelPosition="right"
            content="Save"
            style={{ backgroundcolor: "red" }}
            onClick={onConfirmEditParam}
          />
        </Modal.Actions>
      </Modal>

      <Modal
        size={"small"}
        open={showImageModal}
        onClose={() => setShowImageModal(false)}
      >
        <Modal.Content>
          <Image src={image} />
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowImageModal(false)} positive>
            Close
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

ParametersTable.propTypes = {
  params: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  paginated: PropTypes.bool,
  columnNames: PropTypes.arrayOf(PropTypes.string),
  columnKeys: PropTypes.arrayOf(PropTypes.string),
  editable: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  numberOfItemsPerPage: PropTypes.number,
  cellStyle: PropTypes.shape({}),
  boldIndexes: PropTypes.arrayOf(PropTypes.number),
  onEditParameter: PropTypes.func,
  highlightOnChange: PropTypes.bool,
};

ParametersTable.defaultProps = {
  paginated: false,
  columnNames: [],
  editable: false,
  numberOfItemsPerPage: null,
  highlightOnChange: false,
  cellStyle: {},
  boldIndexes: [],
  onEditParameter: () => {},
};

export default React.memo(ParametersTable);
